import { JSX, Show } from "solid-js";
import c from "class-c";

import CommunityProfile from "@repo/kikoff-models/community/CommunityProfile";

import { useFeatureFlags } from "~/state/features/featureFlags";
import { useProfiles } from "~/state/features/profiles";

import Tag from "../post/Tag";

import Avatar from "./Avatar";

import styles from "./ProfileBadge.module.scss";

declare namespace ProfileBadge {
  type Props = {
    class?: string;
    profileId: CommunityProfile.Id;
    showScore?: boolean;
    anonymous?: boolean;
    // Allows for matching vertical alignment
    after?: JSX.Element;
  };
}

function ProfileBadge({
  class: className,
  profileId,
  showScore,
  anonymous,
  after,
}: D<ProfileBadge.Props>) {
  const [profiles] = useProfiles();
  const [featureFlags] = useFeatureFlags();

  const profile = () => profiles.summaryById(profileId)!;
  const creditScore = () =>
    // When user is enabling the showScore setting, we won't be able to preview
    // the score from the summary, must fall back to full profile.
    profile().creditScore || profiles.byId(profileId)?.creditScore || null;

  return (
    <span class={className}>
      {anonymous ? "Anonymous" : profile()?.displayName}
      <Show when={profile().isAdmin}>
        <Tag
          class="ml-1"
          name="Kikoff"
          color="linear-gradient(273deg, #00B42A 0%, #005937 100%)"
        />
      </Show>
      <Show
        when={
          !anonymous &&
          !profile().isAdmin &&
          featureFlags.enabled("communityCreditScores") &&
          (showScore ?? profile().creditScore! >= 300)
        }
      >
        <Tag
          class={c`ml-1 ${styles[`score:${creditScore()}`]}`}
          name={`${creditScore()}+`}
        />
      </Show>{" "}
      <span class="inline-block">{after}</span>
    </span>
  );
}

ProfileBadge.WithAvatar = WithAvatar;

declare namespace ProfileBadge.WithAvatar {
  type Props = ProfileBadge.Props;
}

function WithAvatar({
  class: className,
  ...props
}: D<ProfileBadge.WithAvatar.Props>) {
  const [profiles] = useProfiles();

  const profile = () => profiles.summaryById(props.profileId)!;

  return (
    <div class="row-center">
      <span class={c`${styles["with-avatar"]} ${className}`}>
        <Avatar
          colorValue={profile().id}
          anonymous={props.anonymous}
          class="mr-0.5"
        />
        <ProfileBadge {...props} />
      </span>
    </div>
  );
}

export default ProfileBadge;
